import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import { UserData } from "@/entities/users/types";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import {
  useCreateContract,
  ContractUpdatePayload
} from "@/pages/contracts/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import useBoundStore from "@/services/store/BoundStore";
import { mapEmptyToNull } from "@/utils/formatting";
import { useProducts } from "@/entities/settings/hooks";
import { useAccountCurrencyResolution } from "@/services/hooks/useCurrencyConversion";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject,
  getFieldNameByDisplayType
} from "@/utils/entityFieldsUtils/formFields";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: UserData;
  onContractCreated?: () => void;
  companyId?: string;
  personId?: string;
  partnerId?: string;
  aeOwner?: string;
  csmOwner?: string;
}

const CreateContractModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  userData,
  onContractCreated,
  companyId,
  personId,
  partnerId,
  aeOwner,
  csmOwner
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const createContract = useCreateContract();
  const { accountCurrency, isPending: isCurrencyPending } =
    useAccountCurrencyResolution();

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);
  const [view, setView] = useState<"deals" | "companies" | "people">("deals");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);

  const products = useProducts().data?.products ?? [];

  const { data: entityFieldsResponse } = useEntityFields(EntityType.CONTRACT);
  const updateEntityField = useUpdateEntityField(EntityType.CONTRACT);

  const users = useBoundStore((state) => state.users.entities);

  const onCompanySelect = (companyId: string) => {
    setSelectedCompanyId(companyId);
  };

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "contracts",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      if (
        typeof formSetValue === "function" &&
        typeof formGetValues === "function"
      ) {
        const isMultiSelect = currentField.displayType === "multiSelect";
        const existingValues =
          formGetValues()[currentFieldForNewOption.fieldKey];
        const safeArray = Array.isArray(existingValues) ? existingValues : [];
        const updatedSelection = isMultiSelect
          ? [...safeArray, optionName]
          : optionName;

        formSetValue(currentFieldForNewOption.fieldKey, updatedSelection, {
          shouldValidate: true
        });
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    setView: (view) => {
      if (view === "companies" || view === "people" || view === "deals") {
        setView(view);
      }
    },
    hasUserEnteredDealName: false,
    onCompanySelect,
    entityType: EntityType.CONTRACT,
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const partnerFieldName = getFieldNameByDisplayType(
    baseSections,
    FieldDisplayType.PARTNERS
  );

  const formSections: FormSectionProps[] = baseSections.map((section) => ({
    ...section,
    fields: section.fields?.map((field) => ({
      ...field,
      labelPosition: "left" as const
    }))
  }));

  // Tracks values this modal auto-populates on open so the dirty-check treats
  // them as defaults. Accumulated alongside the effects that set them — never
  // recomputed from live props — so background refetches (e.g. useProducts)
  // cannot cause the seed to shrink while the form still holds the old value.
  const [autoPopulatedValues, setAutoPopulatedValues] = useState<
    Record<string, unknown>
  >({});

  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => formGetValues?.() || {},
    formSections,
    autoPopulatedValues
  );

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setView("deals");
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setIsFormValid(false);
      setAutoPopulatedValues({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof formSetValue !== "function") return;
    const current =
      typeof formGetValues === "function" ? formGetValues() : undefined;

    const newSeed: Record<string, unknown> = {};
    if (companyId && !current?.companyId) {
      formSetValue("companyId", companyId, { shouldValidate: true });
      setSelectedCompanyId(companyId);
      newSeed.companyId = companyId;
    }
    const resolvedAeOwner = aeOwner || userData?.id;
    if (resolvedAeOwner && !current?.aeOwner) {
      formSetValue("aeOwner", resolvedAeOwner, { shouldValidate: true });
      newSeed.aeOwner = resolvedAeOwner;
    }
    if (csmOwner && !current?.csmOwner) {
      formSetValue("csmOwner", csmOwner, { shouldValidate: true });
      newSeed.csmOwner = csmOwner;
    }
    if (partnerId && partnerFieldName && !current?.[partnerFieldName]) {
      formSetValue(partnerFieldName, partnerId, { shouldValidate: true });
      newSeed[partnerFieldName] = partnerId;
    }
    if (Object.keys(newSeed).length > 0) {
      setAutoPopulatedValues((prev) => ({ ...prev, ...newSeed }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, aeOwner, csmOwner, partnerId, partnerFieldName, formSetValue, formGetValues]);

  useEffect(() => {
    if (
      isOpen &&
      products?.length === 1 &&
      products[0]?.id &&
      typeof formSetValue === "function" &&
      typeof formGetValues === "function"
    ) {
      const singleProductId = products[0].id;
      const currentFormProductId = formGetValues()?.productId;

      if (!currentFormProductId) {
        formSetValue("productId", singleProductId, {
          shouldValidate: true
        });
        setAutoPopulatedValues((prev) => ({ ...prev, productId: singleProductId }));
      }
    }
  }, [isOpen, products, formSetValue, formGetValues]);

  const handleSubmit = async (data: ContractUpdatePayload) => {
    // FormNew submits through a native <form>, so Enter reaches this without
    // passing the footer button's gate.
    if (isCurrencyPending) return;
    setIsSubmitting(true);

    const transformFormData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      ...(personId && { manuallyAddedPeople: [personId] }),
      // Without a currency the backend can derive neither revenue-item total
      // column. A failed settings read still creates without one — the old
      // behaviour — rather than leaving the form permanently unusable.
      ...(accountCurrency && { currency: accountCurrency })
    } as ContractUpdatePayload;

    await createContract.mutateAsync(transformFormData, {
      onSuccess: (response) => {
        toast.success(
          <ToastBody
            title={`${data.name} ${t("contracts.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/contracts/${response?.id}`);
                }}
                variant="primary"
              >
                {t("contracts.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        onContractCreated?.();
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("contracts.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="contracts-create-modal"
        title={
          <Text
            as="div"
            variant="lgSubtitleH1"
            data-test="contracts-create-modal-title"
          >
            {t("contracts.createModal.title")}
          </Text>
        }
        content={
          <div
            className="flex flex-col gap-3 p-6 h-[60vh] overflow-y-auto"
            data-test="contracts-create-modal-content"
          >
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => {
                setIsFormValid(isValid);
              }}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="contracts-create-modal-footer"
          >
            <Button
              data-test="contracts-create-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              data-test="contracts-create-modal-submit-button"
              variant="primary"
              disabled={!isFormValid || isSubmitting || isCurrencyPending}
              onClick={handleCreateNew}
            >
              {t("contracts.createModal.createNew")}
            </Button>
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setView("deals");
          }
        }}
        onSuccess={onCompanyCreation}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
      <UnsavedChangesWarningDialog {...warningDialogProps} />
    </>
  );
};

export default CreateContractModal;
